import React, { useEffect, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { organisation as orgAPI, subscription as subscriptionAPI, stroot } from '@birdi/js-sdk';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface UsageData {
  seatsUsed: number;
  seatsTotal: number | string;
  guestsUsed: number;
  guestsTotal: number | string;
  workspacesUsed: number;
  workspacesTotal: number;
  processingUsed: number;
  processingTotal: number;
  storageUsed: number;
  storageTotal: number;
}

interface GraphData {
  id: string;
  label: string;
  used: number;
  total: number | string;
  color: string;
  unit?: string;
  centerText: string;
  percentageText: string;
}

export const GeneralInfoGraphs: React.FC = () => {
  const meta = useMetaSelector();
  const theme = useTheme() as BirdiTheme;
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const [
          storageRes,
          workspacesRes,
          seatsRes,
          processingRes,
          ...subscriptionResults
        ] = await Promise.all([
          orgAPI.getStorageInfo(stroot('faci1a')),
          orgAPI.getWorkspacesOptimized(stroot('wrki1a')),
          orgAPI.getSeatsCount(stroot('ussea1a')),
          orgAPI.getProcessingGPInfo(stroot('hi7ohd')),
          ...(!!meta.organisation.primaryOrgId
            ? []
            : [
                subscriptionAPI
                  .getCurrentPlan(stroot('phahc4'))
                  .catch(() => ({ body: null })),
              ]),
        ]);

        const storageUsed = parseFloat(
          parseFloat(
            String(storageRes.body?.totalUsedStorage || '0'),
          ).toFixed(1),
        );

        const workspaces = workspacesRes.body?.items;
        const workspacesUsed = Array.isArray(workspaces) ? workspaces.length : 0;

        const seatsUsage = {
          userSeatsUsed: Number(seatsRes.body?.userCount) || 0,
          guestSeatsUsed: Number(seatsRes.body?.guestCount) || 0,
        };

        const processingUsed = Number(processingRes.body?.processingGPUsage) || 0;

        const subscriptionRes = subscriptionResults[0];
        const currentPlan = subscriptionRes && 'body' in subscriptionRes && subscriptionRes.body
          ? subscriptionRes.body
          : null;

        setUsageData({
          seatsUsed: seatsUsage.userSeatsUsed,
          seatsTotal: currentPlan?.basePlan?.userLimitSeats || 0,
          guestsUsed: seatsUsage.guestSeatsUsed,
          guestsTotal: currentPlan?.basePlan?.guestsLimitSeats || 'Unlimited',
          workspacesUsed,
          workspacesTotal: currentPlan?.basePlan?.workspacesLimit || 0,
          processingUsed,
          processingTotal: currentPlan?.basePlan?.processingTermLimitGigapixels || 0,
          storageUsed,
          storageTotal: currentPlan?.basePlan?.storageLimitGigabytes || 0,
        });
      } catch (error) {
        console.error('Error fetching usage data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAllData();
  }, [meta.organisation.primaryOrgId]);

  if (isLoading || !usageData) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
          color: ${theme.mono60};
        `}
      >
        Loading...
      </div>
    );
  }

  const calculatePercentage = (used: number, total: number | string): number => {
    if (total === 'Unlimited' || total === 0) return 0;
    return Math.min(100, Math.round((used / Number(total)) * 100));
  };

  const formatValue = (value: number, unit?: string): string => {
    if (unit === ' GB') {
      // Remove .00 if it's a whole number
      const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
      return `${formatted}${unit}`;
    }
    if (unit === ' GP') {
      // Remove .00 if it's a whole number
      const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
      return `${formatted}${unit}`;
    }
    return value.toString();
  };

  const graphs: GraphData[] = [
    {
      id: 'seats',
      label: 'Seats usage',
      used: usageData.seatsUsed,
      total: usageData.seatsTotal,
      color: theme.ocean,
      centerText: `Seats\n${usageData.seatsUsed}/${usageData.seatsTotal}`,
      percentageText: `${calculatePercentage(usageData.seatsUsed, usageData.seatsTotal)}% used`,
    },
    {
      id: 'guests',
      label: 'Guest access usage',
      used: usageData.guestsUsed,
      total: usageData.guestsTotal,
      color: theme.sunConure,
      centerText: `Guests\n${usageData.guestsUsed}/${usageData.guestsTotal}`,
      percentageText: usageData.guestsTotal === 'Unlimited'
        ? 'Unlimited'
        : `${100 - calculatePercentage(usageData.guestsUsed, usageData.guestsTotal)}% available`,
    },
    {
      id: 'workspaces',
      label: 'Workspace usage',
      used: usageData.workspacesUsed,
      total: usageData.workspacesTotal,
      color: theme.flora,
      centerText: `Workspace\n${usageData.workspacesUsed}/${usageData.workspacesTotal}`,
      percentageText: `${100 - calculatePercentage(usageData.workspacesUsed, usageData.workspacesTotal)}% available`,
    },
    {
      id: 'processing',
      label: 'Processing usage',
      used: usageData.processingUsed,
      total: usageData.processingTotal,
      color: theme.canary,
      unit: ' GP',
      centerText: `Processing credit\n${formatValue(usageData.processingUsed, ' GP')} / ${formatValue(usageData.processingTotal, ' GP')}`,
      percentageText: `${calculatePercentage(usageData.processingUsed, usageData.processingTotal)}% used`,
    },
    {
      id: 'storage',
      label: 'Storage usage',
      used: usageData.storageUsed,
      total: usageData.storageTotal,
      color: theme.midnight,
      unit: ' GB',
      centerText: `Storage\n${formatValue(usageData.storageUsed, ' GB')} / ${formatValue(usageData.storageTotal, ' GB')}`,
      percentageText: `${calculatePercentage(usageData.storageUsed, usageData.storageTotal)}% used`,
    },
  ];

  const renderDonutChart = (graph: GraphData) => {
    const percentage = calculatePercentage(graph.used, graph.total);
    const isUnlimited = graph.total === 'Unlimited';

    // For unlimited, show empty circle (0% filled)
    const data = isUnlimited
      ? [
          { name: 'used', value: 0, label: 'Used' },
          { name: 'remaining', value: 100, label: 'Available' },
        ]
      : [
          { name: 'used', value: percentage, label: 'Used' },
          { name: 'remaining', value: 100 - percentage, label: 'Available' },
        ];

    // Use the same color for both parts, but with different opacity
    // Filled part: full opacity, Empty part: low opacity
    const filledColor = graph.color;

    // Custom tooltip
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const entry = payload[0];
        const isUsed = entry.name === 'used';
        const value = entry.value;
        const label = isUsed
          ? `${percentage}% used`
          : `${100 - percentage}% available`;

        return (
          <div
            css={css`
              background: ${theme.mono0};
              border: 1px solid ${theme.mono30};
              border-radius: 6px;
              padding: 0.5em 0.75em;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
              font-size: 0.85rem;
              color: ${theme.midnight};
              z-index: 10000 !important;
              position: relative;
            `}
          >
            {label}
          </div>
        );
      }
      return null;
    };

    return (
      <div
        key={graph.id}
        css={css`
          background: ${theme.mono0};
          border-radius: 1rem;
          padding: 0.25em;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.15em;
          min-width: 0;
          &:hover {
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
          }
        `}
      >
        <h3
          css={css`
            margin: 0.5em 0.25em 0 0.25em;
            font-size: 0.9rem;
            font-weight: 600;
            color: ${theme.midnight};
            text-align: center;
          `}
        >
          {graph.label}
        </h3>
        <div
          css={css`
            position: relative;
            width: 150px;
            height: 150px;
            flex-shrink: 0;
            aspect-ratio: 1;
            @media (max-width: ${breakpoints.s}) {
              width: 110px;
              height: 110px;
            }
            svg {
              outline: none !important;
              * {
                outline: none !important;
              }
            }
            svg:focus,
            svg:focus-visible,
            svg *:focus,
            svg *:focus-visible {
              outline: none !important;
              border: none !important;
            }
          `}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart
              style={{ outline: 'none' }}
            >
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius="65%"
                outerRadius="90%"
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                stroke="none"
                style={{ outline: 'none' }}
              >
                {data.map((entry, index) => {
                  const isUsed = entry.name === 'used';
                  // Full opacity for used part, low opacity for remaining part
                  const opacity = isUsed ? 1 : 0.15;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={filledColor}
                      fillOpacity={opacity}
                      style={{ outline: 'none' }}
                    />
                  );
                })}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 10000 }}
                offset={-10}
              />
            </PieChart>
          </ResponsiveContainer>
          <div
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              text-align: center;
              pointer-events: none;
            `}
          >
            <div
              css={css`
                font-size: 0.8rem;
                font-weight: 600;
                color: ${theme.midnight};
                line-height: 1.2;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                @media (max-width: ${breakpoints.s}) {
                  font-size: 0.75rem;
                }
                @media (max-width: ${breakpoints.xs}) {
                  font-size: 0.65rem;
                }
              `}
            >
              {graph.centerText.split('\n').map((line, index) => (
                <div key={index}>{line}</div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      css={css`
        display: grid;
        padding: 0;
        grid-template-columns: repeat(5, 1fr);
        gap: 0.5em;
        margin-bottom: 0.5em;
        min-width: 0;

        @media (max-width: ${breakpoints.s}) {
          grid-template-columns: repeat(2, 1fr);
        }
        @media (max-width: ${breakpoints.xs}) {
          grid-template-columns: repeat(2, 1fr);
        }
      `}
    >
      {graphs.map(renderDonutChart)}
    </div>
  );
};

